// 市区町村の検索コンボボックス。候補の絞り込み自体は search.ts の純粋関数に任せ、
// ここは「キーボードとフォーカスの振る舞い」だけを持つ。
import type { Muni } from "../../../schema/muni";
import { DEFAULT_LANG, type Lang } from "../i18n";
import { prefLabel } from "../prefNames";
import { searchMunis } from "../search";
import { escapeHtml } from "../ssr";

export interface SearchBoxOptions {
  input: HTMLInputElement;
  list: HTMLUListElement;
  munis: Muni[];
  lang?: Lang;
  /** 候補行に添える現在指標の値。render 時点の指標を読むため関数で受ける。 */
  describe(muni: Muni): string;
  onSelect(muni: Muni): void;
}

export function createSearchBox(opts: SearchBoxOptions): void {
  const { input, list, munis, describe, onSelect } = opts;
  const lang = opts.lang ?? DEFAULT_LANG;
  // 英語表示では都道府県のローマ字も引き当てる（「Tokyo」で東京都の市区町村が出る）。
  // 市区町村名まで含めるので、確定後に入力欄へ書き戻した表示名でも同じ候補が引ける。
  const alias = (muni: Muni): string => `${prefLabel(muni.pref, lang)}${muni.name}`;
  let hits: Muni[] = [];
  let active = -1;

  function find(query: string): Muni[] {
    return searchMunis(munis, query, undefined, alias);
  }

  function close(): void {
    list.hidden = true;
    list.innerHTML = "";
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
    hits = [];
    active = -1;
  }

  function paintActive(): void {
    const options = list.querySelectorAll<HTMLLIElement>("[role=option]");
    options.forEach((li, i) => li.setAttribute("aria-selected", String(i === active)));
    const current = active >= 0 ? options[active] : undefined;
    if (current) {
      input.setAttribute("aria-activedescendant", current.id);
      current.scrollIntoView({ block: "nearest" });
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  }

  function open(): void {
    if (hits.length === 0) {
      close();
      return;
    }
    list.innerHTML = hits
      .map(
        (m, i) => `<li id="search-opt-${i}" role="option" aria-selected="false" data-index="${i}">
          <span class="opt-name"><span class="opt-pref">${escapeHtml(prefLabel(m.pref, lang))}</span>${escapeHtml(m.name)}</span>
          <span class="opt-value">${escapeHtml(describe(m))}</span>
        </li>`,
      )
      .join("");
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    paintActive();
  }

  function choose(index: number): void {
    const muni = hits[index];
    if (!muni) return;
    input.value = `${prefLabel(muni.pref, lang)}${lang === "ja" ? "" : " "}${muni.name}`;
    close();
    input.blur();
    onSelect(muni);
  }

  input.addEventListener("input", () => {
    hits = find(input.value);
    active = hits.length > 0 ? 0 : -1;
    open();
  });

  input.addEventListener("focus", () => {
    if (input.value !== "") {
      hits = find(input.value);
      active = hits.length > 0 ? 0 : -1;
      open();
    }
  });

  input.addEventListener("keydown", (ev: KeyboardEvent) => {
    if (ev.key === "Escape") {
      close();
      return;
    }
    if (ev.key === "ArrowDown" || ev.key === "ArrowUp") {
      if (list.hidden || hits.length === 0) return;
      ev.preventDefault();
      const step = ev.key === "ArrowDown" ? 1 : -1;
      active = (active + step + hits.length) % hits.length;
      paintActive();
      return;
    }
    if (ev.key === "Enter" && !list.hidden && active >= 0) {
      ev.preventDefault();
      choose(active);
    }
  });

  list.addEventListener("mousedown", (ev) => {
    // blur より先に選択を確定させる
    ev.preventDefault();
    const li = ev.target instanceof Element ? ev.target.closest<HTMLLIElement>("[data-index]") : null;
    if (li) choose(Number(li.dataset.index));
  });

  document.addEventListener("click", (ev) => {
    if (ev.target instanceof Node && !input.contains(ev.target) && !list.contains(ev.target)) close();
  });

  // 入力中でなければ "/" で検索へ飛べる（一般的な検索ショートカット）
  document.addEventListener("keydown", (ev: KeyboardEvent) => {
    if (ev.key !== "/" || ev.metaKey || ev.ctrlKey || ev.altKey) return;
    const el = document.activeElement;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return;
    ev.preventDefault();
    input.focus();
    input.select();
  });
}
